import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { cx } from 'linaria';
import { counterSticky } from 'common/components/product-price-view/sticky.styles';
import { cn, jcm } from 'utils';
import { useIntersectionObserverCallback } from '@navi-toolbox/hooks';
import { useOptimizedImages } from 'common/hooks/use-optimized-images';
import { useBreakpoints } from 'common/hooks';
import {
  getDisplayedName,
  getProductImages,
  getProductId,
  getPrimaryPrice,
  getProductPriceCategory,
  isWebSellable,
} from 'features/product-info/ducks/selectors';
import { ImagePlaceholder } from '@navi-toolbox/ui-components';
import { styles } from '../pdp-showcase/styles';
import { PdpPriceView } from '../pdp-price-view';
import { PdpAddToBasket } from '../pdp-add-to-basket';
import { mediaMobile } from '../pdp-media-gallery/styles';
import { PdpSelectTintColor } from '../pdp-select-tint-color';
import { PushPromoBanner } from 'common/components/push-promo-banner';
import { getShowCatchPushPromo, getYaSplitInfo } from 'features/page-model';
import { getCartItem } from 'features/basket';
import {
  fixedOuterContainerStyle,
  innerContainer,
  innerContainerFixed,
  thumbImg,
  unfixedOuterContainerStyleStyle,
  Basket,
  ToCart,
  pushPromoBanner,
} from './pdp-sticky-card-panel.styles';
import { PriceAndAddToBasketProps } from './types';
import { useTranslation } from 'react-i18next';
import { StickyCardPanelSkeleton } from './sticky-card-panel-skeleton';
import { YandexSplitBanner } from 'features/yandex-split-banner';

export const StickyCardPanel: React.FC<PriceAndAddToBasketProps> = ({
  productDataIsLoading,
  shouldShowTintService,
}) => {
  const { t } = useTranslation();
  const { desktop, largeDesktop } = useBreakpoints() || {
    desktop: false,
    largeDesktop: false,
  };
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const photoSrc = useSelector(getProductImages)?.[0].url ?? '';
  const productName = useSelector(getDisplayedName) ?? '';
  const imgSizes = {
    img: {
      w: '40',
      h: '40',
    },
  };
  const optimizedImage = useOptimizedImages(photoSrc, imgSizes);
  const isSellable = useSelector(isWebSellable);
  const productId = useSelector(getProductId);
  const { price, prevPrice, discountPercent } = useSelector(getPrimaryPrice) || {};
  const priceCategory = useSelector(getProductPriceCategory);
  const showCatchPushPromo = useSelector(getShowCatchPushPromo);
  const cartEntity = useSelector(getCartItem(productId || ''));
  const inBasket = !!cartEntity;
  const quantity = cartEntity?.quantity || 0;
  const yaSplitInfo = useSelector(getYaSplitInfo);

  useEffect(() => {
    if (desktop || largeDesktop) {
      setIsSticky(false);
    } else if (window?.scrollY > (containerRef?.current?.offsetTop ? containerRef?.current?.offsetTop : 0)) {
      setIsSticky(true);
    }
  }, [containerRef, desktop, largeDesktop]);
  useIntersectionObserverCallback(
    containerRef,
    {
      exit: () => {
        if (desktop || largeDesktop) {
          setIsSticky(false);
        } else if (
          !isSticky &&
          window?.scrollY > (containerRef?.current?.offsetTop ? containerRef?.current?.offsetTop : 0)
        ) {
          setIsSticky(true);
        }
      },
      enter: () => {
        if (isSticky) setIsSticky(false);
      },
    },
    [isSticky, setIsSticky, containerRef],
  );

  return (
    <>
      <div className={unfixedOuterContainerStyleStyle} ref={containerRef}>
        {productDataIsLoading ? (
          <StickyCardPanelSkeleton />
        ) : (
          <>
            {isSellable && (
              <div className={innerContainer}>
                {shouldShowTintService && <PdpSelectTintColor />}
                <div data-qa="prices_mf-pdp" className={styles.prices}>
                  <PdpPriceView
                    isStickyPanel={false}
                    className={cn({
                      [styles.whenDataIsLoading.common]: productDataIsLoading,
                    })}
                    inBasket={inBasket}
                    quantity={quantity}
                  />
                </div>
                <Basket isSticky={false}>
                  <ToCart data-qa="to-cart_mf-pdp">
                    <PdpAddToBasket
                      size="l"
                      classnameCounter={styles.counter}
                      isStickyPanel={false}
                      className={jcm({
                        [styles.whenDataIsLoading.noninteractive]: productDataIsLoading,
                      })}
                    />
                  </ToCart>
                </Basket>
              </div>
            )}
          </>
        )}
      </div>
      {yaSplitInfo && <YandexSplitBanner yaSplitInfo={yaSplitInfo} price={price} />}
      {showCatchPushPromo && (
        <div className={pushPromoBanner}>
          <PushPromoBanner
            productName={productName}
            productId={productId}
            productPrice={price}
            productPrevPrice={prevPrice}
            productTag={priceCategory}
            productDiscount={discountPercent}
          />
        </div>
      )}
      {isSticky && !productDataIsLoading && (
        <div className={fixedOuterContainerStyle}>
          <div data-qa="photo_thumbnail_mf-pdp" className={thumbImg}>
            <picture data-qa="picture-thumbnail-sticky-element">
              <source
                media={mediaMobile}
                srcSet={`${optimizedImage.img?.xx} 2x, ${optimizedImage.img?.x} 1x`}
              />
              <ImagePlaceholder
                src={optimizedImage.img?.x || ''}
                srcSet={`${optimizedImage.img?.xx} 2x, ${optimizedImage.img?.x} 1x`}
                alt={productName}
                title={productName}
                className={thumbImg}
                loading="lazy"
                fallbackWidth={40}
                fallbackHeight={40}
                fallbackAlt={t('ui.image.alt')}
              />
            </picture>
          </div>
          <div className={cx(innerContainer, innerContainerFixed)}>
            <div data-qa="prices_mf-pdp-sticky">
              <PdpPriceView
                isStickyPanel={true}
                className={cn(styles.prices, {
                  [styles.whenDataIsLoading.common]: productDataIsLoading,
                })}
                inBasket={inBasket}
                quantity={quantity}
              />
            </div>
            <Basket isSticky={true}>
              <ToCart data-qa="to-cart_mf-pdp-sticky">
                <PdpAddToBasket
                  size="l"
                  classnameCounter={counterSticky}
                  isStickyPanel={true}
                  className={jcm({
                    [styles.whenDataIsLoading.noninteractive]: productDataIsLoading,
                  })}
                />
              </ToCart>
            </Basket>
          </div>
        </div>
      )}
    </>
  );
};
